import {
  getMorphemeMnemonicsFlashcardSectionTabsGate,
  MorphemeMnemonicsFlashcardSectionTabs,
  MorphemeMnemonicsPanelTabButtonsRenderer,
  useMorphemeMnemonicsPanelTabButtonsGate,
} from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._components.tab-buttons";
import { MorphemeMnemonicsTabHostContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels.tab-host-context";
import { MorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import { GraphemePanelsMnemonicsTabContextProvider } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._views.grapheme.mnemonics._context";
import { GraphemeMorphemePanelsContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._context";
import type { TabBarControls } from "@-/dev.improve.haniverse.components.ui.floating-tab-bar";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { useCallback, useContext, useEffect, useMemo } from "react";
import useLatest from "@-/com.npmjs.use-latest";

export type MorphemeMnemonicsViewTabDescriptor = {
  name: string;
  pinyin: string | undefined;
  title: string;
};

export function MorphemeMnemonicsViewTabBar({
  tabButtonList,
  ...tabBarProps
}: TabBarControls & {
  tabButtonList: readonly MorphemeMnemonicsViewTabDescriptor[];
}) {
  const { navigateToTab, state } = tabBarProps;
  const tabActiveIndex = state.index;
  // A Learn card hosts these sections in its Mnemonics binder tab instead of an in-card strip.
  const tabHost = useContext(MorphemeMnemonicsTabHostContext);
  const [setActiveIndex, viewNavigationRef] = useContextSelectors(
    MorphemePanelsContext,
    (c) => c.setActiveIndex,
    (c) => c.viewNavigationRef,
  );

  useEffect(() => {
    setActiveIndex(tabActiveIndex);
  }, [setActiveIndex, tabActiveIndex]);

  const navigationRef = useLatest(navigateToTab);
  const stateRef = useLatest(state);

  const [grapheme] = useContextSelectors(
    GraphemeMorphemePanelsContext,
    (c) => c.readonlySlice.data.grapheme,
  );

  const navigate = useCallback(
    (routeName: string) => {
      const route = state.routes.find((entry) => entry.name === routeName);
      if (route) {
        navigateToTab(route.key);
      }
    },
    [navigateToTab, state.routes],
  );

  const routeNames = useMemo(
    () => state.routes.map((route: (typeof state.routes)[number]) => route.name),
    [state.routes],
  );

  const setActiveIndexRef = useLatest(setActiveIndex);
  const tabButtonListRef = useLatest(tabButtonList);
  const cardSectionTabsGate = getMorphemeMnemonicsFlashcardSectionTabsGate(tabButtonList);
  const panelTabButtonsGate = useMorphemeMnemonicsPanelTabButtonsGate(tabButtonList);
  useEffect(() => {
    viewNavigationRef.current = {
      viewPronunciation: (nextIndex) => {
        const currentState = stateRef.current;
        const currentNavigation = navigationRef.current;
        const route = currentState.routes[nextIndex];
        if (route === undefined || nextIndex === currentState.index) {
          return null;
        }
        currentNavigation(route.key);
        // Advance the shared activeIndex EAGERLY rather than relying solely on the
        // tab-navigation → `setActiveIndex(tabActiveIndex)` effect above. That
        // effect only runs after the tab navigator commits the route change and
        // re-renders the tab bar; under load (a deep view hierarchy + a tool
        // polling the screen, e.g. Maestro) that roundtrip can be starved long
        // enough that the lesson footer never swaps from "Next Pronunciation" to
        // "Next Card", stranding the card. Setting it here makes the footer
        // progress immediately on tap; the effect later sets the same value
        // (idempotent).
        setActiveIndexRef.current(nextIndex);
        const nextTab = tabButtonListRef.current[nextIndex];
        return { pinyin: nextTab?.pinyin };
      },
    };
    return () => {
      viewNavigationRef.current = null;
    };
  }, [navigationRef, setActiveIndexRef, stateRef, tabButtonListRef, viewNavigationRef]);

  return (
    <GraphemePanelsMnemonicsTabContextProvider
      activeIndex={tabActiveIndex}
      morpheme={grapheme}
      navigate={navigate}
      routeNames={routeNames}
      tabButtonList={tabButtonList}
    >
      {tabHost ? (
        cardSectionTabsGate !== null && tabHost.isActive ? (
          <MorphemeMnemonicsFlashcardSectionTabs
            activeIndex={tabActiveIndex}
            gate={cardSectionTabsGate}
            tabHost={tabHost}
          />
        ) : null
      ) : panelTabButtonsGate === null ? null : (
        <MorphemeMnemonicsPanelTabButtonsRenderer
          activeIndex={tabActiveIndex}
          gate={panelTabButtonsGate}
        />
      )}
    </GraphemePanelsMnemonicsTabContextProvider>
  );
}
